// Screen Wake Lock.
//
// Easy to forget and instantly infuriating without it: the phone is on a bench
// and the screen must not sleep between sets.
//
// The lock is dropped by the browser whenever the page is hidden, so it has to
// be re-acquired on visibilitychange rather than held once at session start.

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

const wakeLock = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;

export const wakeLockSupported = Boolean(wakeLock);

let sentinel: WakeLockSentinelLike | null = null;
let wanted = false;

async function acquire(): Promise<void> {
  if (!wakeLock || !wanted || sentinel) return;
  if (document.visibilityState !== 'visible') return;
  try {
    sentinel = await wakeLock.request('screen');
    sentinel.addEventListener('release', () => {
      sentinel = null;
    });
  } catch {
    // denied, or the battery saver refused: nothing to do but let it sleep
    sentinel = null;
  }
}

function onVisibility(): void {
  if (document.visibilityState === 'visible') void acquire();
}

/** Keep the screen awake until `releaseWakeLock` is called. */
export async function keepScreenAwake(): Promise<void> {
  if (wanted) return;
  wanted = true;
  document.addEventListener('visibilitychange', onVisibility);
  await acquire();
}

export async function releaseWakeLock(): Promise<void> {
  wanted = false;
  document.removeEventListener('visibilitychange', onVisibility);
  const current = sentinel;
  sentinel = null;
  try {
    await current?.release();
  } catch {
    // already gone
  }
}
